import React from 'react';
import { Pill as TPill } from '../../../domain/status';
import pillPackageImage from '../../../assets/images/pill/Box.png';
import { range } from '../../../util/number';
import { DraggablePill, DragOrigin, Pill } from './Pill';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { useDrop } from 'react-dnd';
import { DragTargetTypes } from '../Planner/DayTime';
import clsx from 'clsx';

interface IPillPackageProps {
    pill: TPill;
    availableAmount: number;
    onDrop: (pill: TPill, origin: DragOrigin) => void;
    removePillFromOrigin(pill: TPill, origin: DragOrigin): void;
}

const useStyles = createUseStyles({
    root: {
        borderRadius: 20,
        textAlign: 'center',
    },
    box: {
        maxWidth: 150,
        maxHeight: 150,
    },
    container: {
        position: 'relative',
    },
    text: {
        transform: 'rotate(-10deg)',
        display: 'inline-block',
        fontWeight: 'bold',
    },
    textContainer: {
        position: 'absolute',
        width: '68%',
        left: '14%',
        top: '27%',
    },
    pills: {
        display: 'flex',
        justifyContent: 'space-around',
        flexWrap: 'wrap',
        gap: 16,
        '& >:nth-child(4n)': {
            transform: 'rotate(-10deg)',
        },
        '&>:nth-child(3n)': {
            transform: 'rotate(5deg) translate(-5px 8px)',
        },
        '&>:nth-child(2n)': {
            transform: 'translate(8px -6px) rotate(15deg)',
        },
    },
    dropzone: {
        background: '#B6B5D466',
        boxShadow: '0 0 20px #B6B5D466',
    },
});

export const PillPackage: React.FunctionComponent<IPillPackageProps> = props => {
    const styles = useStyles();
    const { t } = useTranslation();

    const [ { canDrop, isOver }, drop ] = useDrop<DraggablePill, unknown, {
        canDrop: boolean;
        isOver: boolean;
    }>(() => ({
        accept: DragTargetTypes.PILL,
        drop: item => {
            props.onDrop(item.pill, item.origin);
            if (item.origin !== 'box') {
                props.removePillFromOrigin(item.pill, item.origin);
            }
        },
        collect: monitor => {
            return ({
                isOver: monitor.isOver(),
                canDrop: monitor.getItem()?.pill?.key === props.pill.key,
            });
        },
        canDrop: (item) => {
            return item.origin !== 'box' && item.pill.key === props.pill.key;
        },
    }));

    return (
        <div ref={drop} className={clsx(styles.root, canDrop && styles.dropzone)}>
            <div className={styles.container}>
                <img src={pillPackageImage} alt="" className={styles.box} />
                <div className={styles.textContainer}>
                    <span className={styles.text}>{t(`effect.${props.pill.key}.name`)}</span>
                </div>
            </div>
            <div className={clsx(styles.pills)}>
                {range(props.availableAmount).map(j => (
                    <Pill pill={props.pill} key={`${props.pill.key}${j}`} origin="box" />
                ))}
            </div>
        </div>
    );
};
